import type { ButtonHTMLAttributes, ReactNode } from "react";

/** "scanner" is the pre-redesign name of the blue go-button; it renders as primary. */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "scanner";
export type ButtonSize = "sm" | "md" | "lg";

const variants: Record<Exclude<ButtonVariant, "scanner">, string> = {
  primary: "bg-accent text-white border-transparent hover:bg-accent-hover",
  secondary: "bg-glass-2 text-ink border-line hover:bg-glass-3 hover:border-line-strong",
  ghost: "bg-transparent text-ink-2 border-transparent hover:bg-glass-2 hover:text-ink",
  danger: "bg-transparent text-danger border-danger/40 hover:bg-danger-soft",
};

// Phone sizes follow the 44px tap minimum; sm stays compact but never under 36.
const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs max-[899px]:h-9",
  md: "h-[38px] px-4 text-[13px] max-[899px]:h-11 max-[899px]:text-sm",
  lg: "h-[46px] px-[22px] text-[15px]",
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  block?: boolean;
  /** In flight: the label stays, a ring spins in the icon slot, clicks are ignored. */
  busy?: boolean;
};

/** Primary is the one action per view; secondary everywhere else. */
export function Button({ variant = "secondary", size = "md", icon, block = false, busy = false, className = "", children, disabled, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={`${block ? "flex w-full" : "inline-flex"} items-center justify-center gap-2 rounded-md border font-semibold tracking-[-0.005em]
        transition duration-150 ease-(--ease-rp) active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100
        focus-visible:outline-none focus-visible:shadow-glow ${variants[variant === "scanner" ? "primary" : variant]} ${sizes[size]} ${className}`}
    >
      {busy ? <ButtonSpinner /> : icon}
      {children}
    </button>
  );
}

/** 14px ring in the button's own text colour, so it reads on every variant. */
function ButtonSpinner() {
  return <span aria-hidden="true" className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-current/25 border-t-current [animation:rp-spin_0.7s_linear_infinite]" />;
}
